import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, Row, Col, Form, Button, Collapse, Tab, Tabs } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import posthog from "posthog-js";
import { axiosBasic } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import { getSelectedCompany } from "../../../../Global/CustomFunctions";
import FetchingSpinner from "../../../../common/FetchingSpinner";
import DashboardPeriodPicker from "../../../dashboard/statistics/DashboardPeriodPicker";
import RoleDashboardStats from "../../../dashboard/statistics/RoleDashboardStats";
import {
  buildPeriodQuery,
  getCurrentMonthPeriod,
  getDashboardRoleType,
  mapAdminDashboardSummary,
} from "../../../dashboard/statistics/dashboardStatisticsUtils";
import "./officeDashboard.scss";
import "./analytics.scss";

const colors = ["#4A90E2", "#FFA726", "#4ECDC4", "#FFD54F", "#66BB6A", "#E57373", "#BA68C8", "#4DD0E1"];
const statusColors = ["#66BB6A", "#FFA726", "#EF5350"];
const DEFAULT_PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 100;

// Must match backend LeaveApprovalStatus enum: Pending=0, Approved=1, Rejected=2, Canceled=3
const LEAVE_STATUS_IDS = [0, 1, 2, 3];
const LEAVE_STATUS_KEYS = ["app.analytics.pending", "app.analytics.approved", "app.analytics.rejected", "app.analytics.canceled"];

function formatDate(d) {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function buildOnLeaveTodayParams(filters) {
  const params = new URLSearchParams();
  if (filters.date) params.set("date", formatDate(filters.date));
  if (filters.startDate) params.set("startDate", formatDate(filters.startDate));
  if (filters.endDate) params.set("endDate", formatDate(filters.endDate));
  if (filters.officeId) params.set("officeId", filters.officeId);
  if (filters.departmentId) params.set("departmentId", filters.departmentId);
  if (filters.departmentIds?.length) filters.departmentIds.forEach((id) => params.append("departmentIds", id));
  const leaveTypeIds = filters.leaveTypeIds ?? [];
  if (leaveTypeIds.length === 1) params.set("leaveTypeId", String(leaveTypeIds[0]));
  else if (leaveTypeIds.length > 1) leaveTypeIds.forEach((id) => params.append("leaveTypeIds", id));
  if (filters.leaveStatusIds?.length) filters.leaveStatusIds.forEach((id) => params.append("leaveStatusIds", id));
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.min(MAX_PAGE_SIZE, Math.max(1, Number(filters.pageSize) || DEFAULT_PAGE_SIZE));
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildLeaveStatisticsParams(filters) {
  const params = new URLSearchParams();
  if (filters.statisticsStartDate) params.set("statisticsStartDate", formatDate(filters.statisticsStartDate));
  if (filters.statisticsEndDate) params.set("statisticsEndDate", formatDate(filters.statisticsEndDate));
  if (filters.officeId) params.set("officeId", filters.officeId);
  if (filters.departmentId) params.set("departmentId", filters.departmentId);
  if (filters.departmentIds?.length) filters.departmentIds.forEach((id) => params.append("departmentIds", id));
  const leaveTypeIds = filters.leaveTypeIds ?? [];
  if (leaveTypeIds.length === 1) params.set("leaveTypeId", String(leaveTypeIds[0]));
  else if (leaveTypeIds.length > 1) leaveTypeIds.forEach((id) => params.append("leaveTypeIds", id));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function mapLeaveEntry(e) {
  return {
    leaveTypeName: e.leaveTypeName ?? e.LeaveTypeName ?? "",
    startDate: e.startDate ?? e.StartDate ?? "",
    endDate: e.endDate ?? e.EndDate ?? "",
    leaveStatus: e.leaveStatus ?? e.LeaveStatus ?? "",
    leaveStatusId: e.leaveStatusId ?? e.LeaveStatusId,
  };
}

function mapUserOnLeave(u) {
  return {
    userId: u.userId ?? u.UserId ?? u.id ?? u.Id,
    fullName: u.fullName ?? u.FullName ?? u.name ?? u.Name ?? "",
    email: u.email ?? u.Email ?? "",
    department: u.department ?? u.Department ?? "",
    workplace: u.workplace ?? u.Workplace ?? "",
    imageUrl: u.imageUrl ?? u.ImageUrl ?? "",
    leaveEntries: Array.isArray(u.leaveEntries ?? u.LeaveEntries) ? (u.leaveEntries ?? u.LeaveEntries).map(mapLeaveEntry) : [],
  };
}

function mapWeeklyOrMonthly(item, labelKeys, includePeriodStart = false) {
  const label = labelKeys.reduce((acc, k) => acc || item[k] || item[k.charAt(0).toUpperCase() + k.slice(1)], "") || "—";
  const out = {
    label: String(label),
    totalLeaveDays: Number(item.totalLeaveDays ?? item.TotalLeaveDays ?? item.count ?? item.Count ?? 0),
    distinctUsersCount: Number(item.distinctUsersCount ?? item.DistinctUsersCount ?? 0),
  };
  if (includePeriodStart) {
    const ps = item.periodStart ?? item.PeriodStart;
    if (ps != null) out.periodStart = typeof ps === "string" ? ps : (ps?.toString?.() ?? null);
  }
  return out;
}

export default function Analytics() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isLeaveDashboardPage = location.pathname === "/leave/dashboard";
  const { REST_API } = config;
  const LEAVE_STATUS_OPTIONS = LEAVE_STATUS_IDS.map((id, i) => ({ id, label: t(LEAVE_STATUS_KEYS[i]) }));

  const [activeTab, setActiveTab] = useState("dashboard");

  // Shared dropdown data
  const [offices, setOffices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Tab 1: Dashboard Summary
  const defaultDashboardPeriod = getCurrentMonthPeriod();
  const [dashboardPeriodDraft, setDashboardPeriodDraft] = useState(defaultDashboardPeriod);
  const [dashboardPeriodApplied, setDashboardPeriodApplied] = useState(defaultDashboardPeriod);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const dashboardRoleType = getDashboardRoleType();

  // Tab 2: On leave grid (single date or startDate/endDate range)
  const [onLeaveItems, setOnLeaveItems] = useState([]);
  const [onLeaveTotalCount, setOnLeaveTotalCount] = useState(0);
  const [onLeaveLoading, setOnLeaveLoading] = useState(false);
  const [onLeaveError, setOnLeaveError] = useState(null);
  const [onLeaveFilterOpen, setOnLeaveFilterOpen] = useState(false);
  const [onLeaveFilters, setOnLeaveFilters] = useState({
    date: null,
    startDate: null,
    endDate: null,
    officeId: "",
    departmentId: "",
    departmentIds: [],
    leaveTypeIds: [],
    leaveStatusIds: [],
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [onLeaveApplied, setOnLeaveApplied] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE });

  // Tab 3: Leave Statistics — default range: Jan 1 to Dec 31 of current year
  const getDefaultLeaveStatsDateRange = () => {
    const y = new Date().getFullYear();
    return { statisticsStartDate: new Date(y, 0, 1), statisticsEndDate: new Date(y, 11, 31) };
  };
  const defaultLeaveStatsRange = getDefaultLeaveStatsDateRange();
  const [leaveStatsData, setLeaveStatsData] = useState(null);
  const [leaveStatsLoading, setLeaveStatsLoading] = useState(false);
  const [leaveStatsError, setLeaveStatsError] = useState(null);
  const [leaveStatsFilterOpen, setLeaveStatsFilterOpen] = useState(false);
  const [leaveStatsFilters, setLeaveStatsFilters] = useState({
    ...defaultLeaveStatsRange,
    officeId: "",
    departmentId: "",
    departmentIds: [],
    leaveTypeIds: [],
  });
  const [leaveStatsApplied, setLeaveStatsApplied] = useState({ ...defaultLeaveStatsRange, leaveTypeIds: [] });

  const fetchDashboardSummary = useCallback(async () => {
    if (isLeaveDashboardPage && dashboardRoleType !== "admin") {
      setDashboardData(null);
      setDashboardError(t("app.analytics.notAuthorized"));
      setDashboardLoading(false);
      return;
    }

    const endpoints = {
      admin: REST_API.Statistics.DashboardSummary,
      manager: REST_API.Statistics.ManagerDashboardSummary,
      user: REST_API.Statistics.UserDashboardSummary,
    };
    const endpoint = endpoints[dashboardRoleType] ?? endpoints.user;

    try {
      setDashboardLoading(true);
      setDashboardError(null);
      const qs = buildPeriodQuery(dashboardPeriodApplied.periodFrom, dashboardPeriodApplied.periodTo);
      const res = await axiosBasic.get(endpoint + qs);
      if (res.data?.success && res.data.data) {
        setDashboardData(mapAdminDashboardSummary(res.data.data));
      } else {
        setDashboardData(null);
        setDashboardError(res.data?.message ?? t("app.analytics.failedToLoadDashboard"));
      }
    } catch (err) {
      setDashboardData(null);
      setDashboardError(err.response?.data?.message ?? err?.message ?? t("app.analytics.failedToLoadDashboard"));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("analyticsDashboardSummary", { type: "exception", reason: err?.message });
      }
    } finally {
      setDashboardLoading(false);
    }
  }, [
    isLeaveDashboardPage,
    dashboardRoleType,
    dashboardPeriodApplied,
    REST_API.Statistics.DashboardSummary,
    REST_API.Statistics.ManagerDashboardSummary,
    REST_API.Statistics.UserDashboardSummary,
    t,
  ]);

  const fetchOnLeaveToday = useCallback(async () => {
    try {
      setOnLeaveLoading(true);
      setOnLeaveError(null);
      const qs = buildOnLeaveTodayParams(onLeaveApplied);
      const res = await axiosBasic.get(REST_API.Statistics.OnLeaveGrid + qs);
      const data = res.data?.data ?? res.data;
      const items = data?.items ?? data?.Items ?? [];
      const totalCount = data?.totalCount ?? data?.TotalCount ?? 0;
      setOnLeaveItems(Array.isArray(items) ? items.map(mapUserOnLeave) : []);
      setOnLeaveTotalCount(Number(totalCount));
    } catch (err) {
      setOnLeaveError(err.response?.data?.message ?? err?.message ?? t("app.analytics.failedToLoadGrid"));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("analyticsOnLeaveGrid", { type: "exception", reason: err?.message });
      }
    } finally {
      setOnLeaveLoading(false);
    }
  }, [onLeaveApplied, REST_API.Statistics.OnLeaveGrid, t]);

  const fetchLeaveStatistics = useCallback(async () => {
    try {
      setLeaveStatsLoading(true);
      setLeaveStatsError(null);
      const qs = buildLeaveStatisticsParams(leaveStatsApplied);
      const res = await axiosBasic.get(REST_API.Statistics.LeaveStatistics + qs);
      if (res.data?.success && res.data.data) {
        const d = res.data.data;
        const weekly = d.weeklyLeaveStatistics ?? d.WeeklyLeaveStatistics ?? [];
        const monthly = d.monthlyLeaveStatistics ?? d.MonthlyLeaveStatistics ?? [];
        setLeaveStatsData({
          weeklyLeaveStatistics: weekly.map((item) => mapWeeklyOrMonthly(item, ["label", "weekLabel", "date", "week"], true)),
          monthlyLeaveStatistics: monthly.map((item) => mapWeeklyOrMonthly(item, ["label", "monthLabel", "month", "date"])),
          leaveStatusOverview: (d.leaveStatusOverview ?? d.LeaveStatusOverview ?? []).map((ls) => ({
            status: ls.status ?? ls.Status ?? "",
            statusId: ls.statusId ?? ls.StatusId,
            count: ls.count ?? ls.Count ?? 0,
          })),
          leaveTypesOverview: (d.leaveTypesOverview ?? d.LeaveTypesOverview ?? []).map((lt) => ({
            type: lt.name ?? lt.Name ?? lt.type ?? "",
            count: lt.count ?? lt.Count ?? 0,
          })),
        });
      } else {
        setLeaveStatsError(res.data?.message ?? t("app.analytics.failedToLoadStatistics"));
      }
    } catch (err) {
      setLeaveStatsError(err.response?.data?.message ?? err?.message ?? t("app.analytics.failedToLoadStatistics"));
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("analyticsLeaveStatistics", { type: "exception", reason: err?.message });
      }
    } finally {
      setLeaveStatsLoading(false);
    }
  }, [leaveStatsApplied, REST_API.Statistics.LeaveStatistics, t]);

  useEffect(() => {
    const companyId = getSelectedCompany()?.id ?? getSelectedCompany()?.Id;
    if (!companyId) return;
    axiosBasic.get(REST_API.Office.GetCompanyOffices + "?companyId=" + companyId).then((r) => {
      if (r.data?.success && Array.isArray(r.data.data)) setOffices((r.data.data || []).filter((o) => o.isActive !== false && o.isDeleted !== true));
    }).catch(() => {});
    axiosBasic.get(REST_API.Department.Get).then((r) => {
      if (r.data?.success && Array.isArray(r.data.data)) setDepartments((r.data.data || []).map((i) => ({ id: i.id ?? i.Id, name: i.name ?? i.Name })));
    }).catch(() => {});
    axiosBasic.get(REST_API.LeaveType.GetActiveLeaveTypes).then((r) => {
      if (r.data?.success && Array.isArray(r.data.data)) setLeaveTypes((r.data.data || []).map((lt) => ({ id: lt.id ?? lt.Id, name: lt.name ?? lt.Name ?? "" })));
    }).catch(() => {});
  }, [REST_API.Office.GetCompanyOffices, REST_API.Department.Get, REST_API.LeaveType.GetActiveLeaveTypes]);

  useEffect(() => {
    if (activeTab === "dashboard" && isLeaveDashboardPage) fetchDashboardSummary();
  }, [activeTab, isLeaveDashboardPage, fetchDashboardSummary]);
  useEffect(() => {
    if (activeTab === "onLeave") fetchOnLeaveToday();
  }, [activeTab, onLeaveApplied, fetchOnLeaveToday]);
  useEffect(() => {
    if (activeTab === "leaveStats") fetchLeaveStatistics();
  }, [activeTab, leaveStatsApplied, fetchLeaveStatistics]);

  const applyOnLeaveFilters = () => {
    setOnLeaveApplied({
      ...onLeaveFilters,
      leaveTypeIds: Array.isArray(onLeaveFilters.leaveTypeIds) ? onLeaveFilters.leaveTypeIds.map(Number).filter((n) => !Number.isNaN(n)) : [],
      leaveStatusIds: Array.isArray(onLeaveFilters.leaveStatusIds) ? onLeaveFilters.leaveStatusIds.map(Number).filter((n) => !Number.isNaN(n)) : [],
      page: 1,
      pageSize: Math.min(MAX_PAGE_SIZE, Math.max(1, Number(onLeaveFilters.pageSize) || DEFAULT_PAGE_SIZE)),
    });
  };
  const clearOnLeaveFilters = () => {
    setOnLeaveFilters({ date: null, startDate: null, endDate: null, officeId: "", departmentId: "", departmentIds: [], leaveTypeIds: [], leaveStatusIds: [], page: 1, pageSize: DEFAULT_PAGE_SIZE });
    setOnLeaveApplied({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  };
  const applyLeaveStatsFilters = () => setLeaveStatsApplied({ ...leaveStatsFilters, leaveTypeIds: Array.isArray(leaveStatsFilters.leaveTypeIds) ? leaveStatsFilters.leaveTypeIds.map(Number).filter((n) => !Number.isNaN(n)) : [] });
  const clearLeaveStatsFilters = () => {
    const range = getDefaultLeaveStatsDateRange();
    setLeaveStatsFilters({ ...range, officeId: "", departmentId: "", departmentIds: [], leaveTypeIds: [] });
    setLeaveStatsApplied({ ...range, leaveTypeIds: [] });
  };

  const onLeaveTodayPages = Math.max(1, Math.ceil(onLeaveTotalCount / (onLeaveApplied.pageSize || DEFAULT_PAGE_SIZE)));
  const currentOnLeavePage = Math.min(Math.max(1, Number(onLeaveApplied.page) || 1), onLeaveTodayPages);

  const renderOrganizationUsersStats = (d) => (
    <Row className="g-2 g-md-3">
      <Col xs={12} sm={6} md={4}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(255, 167, 38, 0.08)" }}>
          <i className="bi bi-building analytics-stat-icon" style={{ color: "#FFA726" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.totalWorkplaces}</h3>
          <div className="text-muted small">{t("app.analytics.totalWorkplaces")}</div>
        </div>
      </Col>
      <Col xs={12} sm={6} md={4}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(78, 205, 196, 0.08)" }}>
          <i className="bi bi-diagram-3-fill analytics-stat-icon" style={{ color: "#4ECDC4" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.totalDepartments}</h3>
          <div className="text-muted small">{t("app.analytics.totalDepartments")}</div>
        </div>
      </Col>
      <Col xs={12} sm={6} md={4}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(102, 187, 106, 0.08)" }}>
          <i className="bi bi-person-check-fill analytics-stat-icon" style={{ color: "#66BB6A" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.totalUsers}</h3>
          <div className="text-muted small">{t("app.analytics.totalUsers")}</div>
        </div>
      </Col>
    </Row>
  );

  const renderLeaveSummaryStats = (d) => (
    <Row className="g-2 g-md-4">
      <Col xs={12} sm={6} md={3}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(74, 144, 226, 0.08)" }}>
          <i className="bi bi-tag-fill analytics-stat-icon" style={{ color: "#4A90E2" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.totalLeaveTypes}</h3>
          <div className="text-muted small">{t("app.analytics.leaveTypes")}</div>
        </div>
      </Col>
      <Col xs={12} sm={6} md={3}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(229, 115, 115, 0.08)" }}>
          <i className="bi bi-calendar-x-fill analytics-stat-icon" style={{ color: "#E57373" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.personsOnLeaveToday}</h3>
          <div className="text-muted small">{t("app.analytics.personsOnLeaveToday")}</div>
        </div>
      </Col>
      <Col xs={12} sm={6} md={3}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(255, 213, 79, 0.08)" }}>
          <i className="bi bi-calendar2-event analytics-stat-icon" style={{ color: "#FFD54F" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.leaveRequestsToday}</h3>
          <div className="text-muted small">{t("app.analytics.leaveRequestsToday")}</div>
        </div>
      </Col>
      <Col xs={12} sm={6} md={3}>
        <div className="analytics-stat-box text-center p-2 p-md-3 rounded-3 h-100" style={{ background: "rgba(255, 167, 38, 0.08)" }}>
          <i className="bi bi-hourglass-split analytics-stat-icon" style={{ color: "#FFA726" }} />
          <h3 className="fw-bold mt-2 mb-1 analytics-stat-value">{d.pendingLeaveRequestsToday ?? 0}</h3>
          <div className="text-muted small">{t("app.analytics.pendingLeaveRequestsToday")}</div>
        </div>
      </Col>
    </Row>
  );

  const renderHeader = () => (
    <div className="office-header-bar analytics-header">
      <div className="office-header-content">
        <div className="office-header-left">
          <div className="office-header-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white" viewBox="0 0 16 16" style={{ margin: 0 }}>
              <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
            </svg>
          </div>
          <div className="office-header-text">
            <div className="d-flex align-items-center mb-2 flex-wrap" style={{ gap: "12px" }}>
              <h1 className="office-page-title mb-0">
                {isLeaveDashboardPage ? t("app.analytics.leaveDashboardTitle") : t("app.analytics.title")}
              </h1>
              {!isLeaveDashboardPage && (
                <span className="analytics-badge">{t("app.analytics.dashboard")}</span>
              )}
            </div>
            <p className="office-page-description d-none d-md-block mb-0">
              {isLeaveDashboardPage
                ? t("app.analytics.leaveDashboardDescription")
                : t("app.officeDashboard.description")}
            </p>
          </div>
        </div>
        {!isLeaveDashboardPage && (
          <button type="button" className="btn btn-outline-secondary analytics-back-btn" onClick={() => history.push("/workplaces")}>
            <i className="bi bi-arrow-left me-2" />{t("app.analytics.backToWorkplaces")}
          </button>
        )}
      </div>
    </div>
  );

  const applyDashboardPeriod = () => {
    setDashboardPeriodApplied({
      periodFrom: dashboardPeriodDraft.periodFrom,
      periodTo: dashboardPeriodDraft.periodTo,
    });
  };

  const renderTab1Dashboard = () => {
    if (isLeaveDashboardPage) {
      if (dashboardRoleType !== "admin") {
        return (
          <div className="alert alert-warning">
            <i className="bi bi-shield-exclamation me-2" />
            {t("app.analytics.notAuthorized")}
          </div>
        );
      }

      if (dashboardLoading && !dashboardData) {
        return <div className="py-5"><FetchingSpinner text={t("app.analytics.loading")} /></div>;
      }

      return (
        <>
          <DashboardPeriodPicker
            periodFrom={dashboardPeriodDraft.periodFrom}
            periodTo={dashboardPeriodDraft.periodTo}
            onPeriodFromChange={(d) => setDashboardPeriodDraft((p) => ({ ...p, periodFrom: d }))}
            onPeriodToChange={(d) => setDashboardPeriodDraft((p) => ({ ...p, periodTo: d }))}
            onApply={applyDashboardPeriod}
            resolvedPeriod={dashboardData}
          />
          {dashboardError && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2" />
              {t("app.analytics.errorLabel")}: {dashboardError}
            </div>
          )}
          {!dashboardError && dashboardData && (
            <Card className="shadow-sm border-0 mb-4 analytics-card">
              <Card.Header className="bg-light border-0 py-2 px-2 px-md-3">
                <span className="fw-semibold text-secondary small text-uppercase">
                  <i className="bi bi-calendar-check me-2" />
                  {t("app.analytics.leaveSummaryToday")}
                </span>
              </Card.Header>
              <Card.Body className="pt-2 pb-3 px-2 px-md-3">{renderLeaveSummaryStats(dashboardData)}</Card.Body>
            </Card>
          )}
        </>
      );
    }

    return <RoleDashboardStats />;
  };

  const renderOnLeaveFilters = () => (
    <Card className="shadow-sm border-0 mb-4 analytics-filters-card">
      <Card.Header className="d-flex align-items-center justify-content-between py-2 px-2 px-md-3" style={{ cursor: "pointer", background: "linear-gradient(135deg, #f8f9fa 0%, #fff 100%)" }} onClick={() => setOnLeaveFilterOpen((o) => !o)}>
        <span className="fw-semibold small"><i className="bi bi-funnel me-2" />{t("app.analytics.filters")}</span>
        <i className={`bi bi-chevron-${onLeaveFilterOpen ? "up" : "down"}`} />
      </Card.Header>
      <Collapse in={onLeaveFilterOpen}>
        <Card.Body className="pt-2 pb-3 px-2 px-md-3">
          <Row className="g-2 g-md-3">
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group style={{ overflow: "visible" }} className="mb-0">
                <Form.Label className="small text-muted">{t("app.analytics.date")}</Form.Label>
                <DatePicker selected={onLeaveFilters.date} onChange={(d) => setOnLeaveFilters((f) => ({ ...f, date: d }))} placeholderText={t("app.analytics.todaySingleDay")} dateFormat="yyyy-MM-dd" className="form-control form-control-sm" isClearable popperClassName="analytics-datepicker-popper" popperPlacement="auto" />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group style={{ overflow: "visible" }}>
                <Form.Label className="small text-muted">{t("app.analytics.startDate")}</Form.Label>
                <DatePicker selected={onLeaveFilters.startDate} onChange={(date) => setOnLeaveFilters((f) => ({ ...f, startDate: date }))} placeholderText={t("app.analytics.optional")} dateFormat="yyyy-MM-dd" className="form-control form-control-sm" isClearable popperClassName="analytics-datepicker-popper" popperPlacement="auto" />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group style={{ overflow: "visible" }}>
                <Form.Label className="small text-muted">{t("app.analytics.endDate")}</Form.Label>
                <DatePicker selected={onLeaveFilters.endDate} onChange={(date) => setOnLeaveFilters((f) => ({ ...f, endDate: date }))} placeholderText={t("app.analytics.optional")} dateFormat="yyyy-MM-dd" className="form-control form-control-sm" isClearable popperClassName="analytics-datepicker-popper" popperPlacement="auto" />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.workplace")}</Form.Label>
                <Form.Select value={onLeaveFilters.officeId} onChange={(e) => setOnLeaveFilters((f) => ({ ...f, officeId: e.target.value }))} size="sm">
                  <option value="">{t("app.analytics.all")}</option>{offices.map((o) => <option key={o.id ?? o.Id} value={o.id ?? o.Id}>{o.name ?? o.Name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.department")}</Form.Label>
                <Form.Select value={onLeaveFilters.departmentId} onChange={(e) => setOnLeaveFilters((f) => ({ ...f, departmentId: e.target.value }))} size="sm">
                  <option value="">{t("app.analytics.all")}</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.leaveTypesLabel")}</Form.Label>
                <Form.Select multiple value={(onLeaveFilters.leaveTypeIds || []).map(String)} onChange={(e) => setOnLeaveFilters((f) => ({ ...f, leaveTypeIds: Array.from(e.target.selectedOptions, (opt) => Number(opt.value)) }))} size="sm" className="analytics-multiselect" style={{ minHeight: 76 }}>
                  {leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
                </Form.Select>
                <Form.Text className="text-muted small">{t("app.analytics.ctrlClick")}</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.status")}</Form.Label>
                <Form.Select multiple value={(onLeaveFilters.leaveStatusIds || []).map(String)} onChange={(e) => setOnLeaveFilters((f) => ({ ...f, leaveStatusIds: Array.from(e.target.selectedOptions, (opt) => Number(opt.value)) }))} size="sm" className="analytics-multiselect" style={{ minHeight: 76 }}>
                  {LEAVE_STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </Form.Select>
                <Form.Text className="text-muted small">{t("app.analytics.ctrlClick")}</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} lg={2} className="analytics-filter-buttons-col">
              <div className="analytics-filter-buttons">
                <Button variant="primary" onClick={applyOnLeaveFilters} className="analytics-filter-btn" size="sm">{t("app.analytics.apply")}</Button>
                <Button variant="outline-secondary" onClick={clearOnLeaveFilters} className="analytics-filter-btn" size="sm">{t("app.analytics.clear")}</Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Collapse>
    </Card>
  );

  const renderOnLeaveGridRow = (u, idx) => {
    const entryStatusColor = (status) => (status || "").toLowerCase() === "approved" ? "#66BB6A" : (status || "").toLowerCase() === "rejected" ? "#EF5350" : "#FFA726";
    return (
      <tr key={u.userId ?? idx}>
        <td className="align-middle">
          <div className="d-inline-block position-relative">
            {u.imageUrl ? (
              <>
                <img src={u.imageUrl} alt="" className="rounded-circle" style={{ width: 40, height: 40, objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; const fallback = e.target.nextElementSibling; if (fallback) fallback.classList.remove("d-none"); }} />
                <div className="d-none rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40, fontSize: "1rem" }}>{(u.fullName || "?").charAt(0).toUpperCase()}</div>
              </>
            ) : (
              <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white" style={{ width: 40, height: 40, fontSize: "1rem" }}>{(u.fullName || "?").charAt(0).toUpperCase()}</div>
            )}
          </div>
        </td>
        <td className="fw-medium align-middle analytics-onleave-name-cell" title={u.fullName || "—"}>
          <span className="analytics-onleave-name-text">{u.fullName || "—"}</span>
        </td>
        <td className="text-muted d-none d-md-table-cell small align-middle">{u.email || "—"}</td>
        <td className="d-none d-sm-table-cell align-middle">{u.department || "—"}</td>
        <td className="d-none d-lg-table-cell align-middle">{u.workplace || "—"}</td>
        <td className="align-middle">
          {u.leaveEntries?.length ? (
            <div className="d-flex flex-wrap gap-1">
              {u.leaveEntries.map((entry, ei) => {
                const startStr = entry.startDate ? new Date(entry.startDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
                const endStr = entry.endDate ? new Date(entry.endDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
                const status = entry.leaveStatus || "";
                return (
                  <span key={ei} className="d-inline-flex align-items-center flex-wrap gap-1 me-1 mb-1">
                    <span className="badge bg-secondary" title={`${entry.leaveTypeName || "—"}: ${startStr} – ${endStr}`}>{entry.leaveTypeName || "—"}: {startStr} – {endStr}</span>
                    {status && <span className="badge rounded-pill" style={{ fontSize: "0.7rem", background: entryStatusColor(status), color: "#fff" }}>{status}</span>}
                  </span>
                );
              })}
            </div>
          ) : <span className="text-muted">—</span>}
        </td>
      </tr>
    );
  };

  const renderTab2OnLeave = () => (
    <>
      {renderOnLeaveFilters()}
      <Card className="shadow-sm border-0 mb-4 w-100 analytics-card overflow-hidden">
        <Card.Body className="px-2 px-md-3">
          <h5 className="fw-bold mb-2 analytics-section-title"><i className="bi bi-calendar-x me-2" style={{ color: "#E57373" }} />{t("app.analytics.usersOnLeave")}</h5>
          <p className="mb-3">
            <span className="badge rounded-pill px-3 py-2 analytics-count-badge" style={{ fontSize: "0.9rem", background: "#E57373", color: "#fff" }}>{onLeaveTotalCount} {onLeaveTotalCount === 1 ? t("app.analytics.person") : t("app.analytics.persons")}</span>
          </p>
          {onLeaveLoading && <FetchingSpinner text={t("app.analytics.loading")} />}
          {!onLeaveLoading && onLeaveError && <div className="alert alert-danger">{onLeaveError}</div>}
          {!onLeaveLoading && !onLeaveError && (onLeaveItems.length > 0 ? (
            <>
              <div className="table-responsive analytics-table-wrapper">
                <table className="table table-hover table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr><th className="border-0" style={{ width: 56 }}>{t("app.analytics.avatar")}</th><th className="border-0">{t("app.analytics.name")}</th><th className="border-0 d-none d-md-table-cell">{t("app.analytics.email")}</th><th className="border-0 d-none d-sm-table-cell">{t("app.analytics.department")}</th><th className="border-0 d-none d-lg-table-cell">{t("app.analytics.workplace")}</th><th className="border-0">{t("app.analytics.leave")}</th></tr>
                  </thead>
                  <tbody>{onLeaveItems.map(renderOnLeaveGridRow)}</tbody>
                </table>
              </div>
              {onLeaveTotalCount > 0 && (
                <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-2 border-top analytics-pagination">
                  <span className="text-muted small text-center text-sm-start">{t("app.analytics.showing")} {((currentOnLeavePage - 1) * (onLeaveApplied.pageSize || DEFAULT_PAGE_SIZE)) + 1}–{Math.min(currentOnLeavePage * (onLeaveApplied.pageSize || DEFAULT_PAGE_SIZE), onLeaveTotalCount)} {t("app.analytics.of")} {onLeaveTotalCount}</span>
                  <div className="d-flex align-items-center justify-content-center flex-wrap gap-1">
                    <Button variant="outline-secondary" size="sm" disabled={currentOnLeavePage <= 1} onClick={() => setOnLeaveApplied((p) => ({ ...p, page: 1 }))}>{t("app.analytics.first")}</Button>
                    <Button variant="outline-secondary" size="sm" disabled={currentOnLeavePage <= 1} onClick={() => setOnLeaveApplied((p) => ({ ...p, page: currentOnLeavePage - 1 }))}>{t("app.analytics.prev")}</Button>
                    <span className="px-2 small fw-medium">{t("app.analytics.page")} {currentOnLeavePage} {t("app.analytics.of")} {onLeaveTodayPages}</span>
                    <Button variant="outline-secondary" size="sm" disabled={currentOnLeavePage >= onLeaveTodayPages} onClick={() => setOnLeaveApplied((p) => ({ ...p, page: currentOnLeavePage + 1 }))}>{t("app.analytics.next")}</Button>
                    <Button variant="outline-secondary" size="sm" disabled={currentOnLeavePage >= onLeaveTodayPages} onClick={() => setOnLeaveApplied((p) => ({ ...p, page: onLeaveTodayPages }))}>{t("app.analytics.last")}</Button>
                  </div>
                </div>
              )}
            </>
          ) : <p className="text-muted mb-0">{t("app.analytics.noUsersOnLeave")}</p>)}
        </Card.Body>
      </Card>
    </>
  );

  const renderLeaveStatsFilters = () => (
    <Card className="shadow-sm border-0 mb-4 analytics-filters-card">
      <Card.Header className="d-flex align-items-center justify-content-between py-2 px-2 px-md-3" style={{ cursor: "pointer", background: "linear-gradient(135deg, #f8f9fa 0%, #fff 100%)" }} onClick={() => setLeaveStatsFilterOpen((o) => !o)}>
        <span className="fw-semibold small"><i className="bi bi-funnel me-2" />{t("app.analytics.filters")}</span>
        <i className={`bi bi-chevron-${leaveStatsFilterOpen ? "up" : "down"}`} />
      </Card.Header>
      <Collapse in={leaveStatsFilterOpen}>
        <Card.Body className="pt-2 pb-3 px-2 px-md-3">
          <Row className="g-2 g-md-3">
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group style={{ overflow: "visible" }}>
                <Form.Label className="small text-muted">{t("app.analytics.statisticsFrom")}</Form.Label>
                <DatePicker selected={leaveStatsFilters.statisticsStartDate} onChange={(date) => setLeaveStatsFilters((f) => ({ ...f, statisticsStartDate: date }))} placeholderText={t("app.analytics.currentYear")} dateFormat="yyyy-MM-dd" className="form-control form-control-sm" isClearable popperClassName="analytics-datepicker-popper" popperPlacement="auto" />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group style={{ overflow: "visible" }}>
                <Form.Label className="small text-muted">{t("app.analytics.statisticsTo")}</Form.Label>
                <DatePicker selected={leaveStatsFilters.statisticsEndDate} onChange={(date) => setLeaveStatsFilters((f) => ({ ...f, statisticsEndDate: date }))} placeholderText={t("app.analytics.currentYear")} dateFormat="yyyy-MM-dd" className="form-control form-control-sm" isClearable popperClassName="analytics-datepicker-popper" popperPlacement="auto" />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.workplace")}</Form.Label>
                <Form.Select value={leaveStatsFilters.officeId} onChange={(e) => setLeaveStatsFilters((f) => ({ ...f, officeId: e.target.value }))} size="sm">
                  <option value="">{t("app.analytics.all")}</option>{offices.map((o) => <option key={o.id ?? o.Id} value={o.id ?? o.Id}>{o.name ?? o.Name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.department")}</Form.Label>
                <Form.Select value={leaveStatsFilters.departmentId} onChange={(e) => setLeaveStatsFilters((f) => ({ ...f, departmentId: e.target.value }))} size="sm">
                  <option value="">{t("app.analytics.all")}</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={6} lg={2}>
              <Form.Group><Form.Label className="small text-muted">{t("app.analytics.leaveTypesLabel")}</Form.Label>
                <Form.Select multiple value={(leaveStatsFilters.leaveTypeIds || []).map(String)} onChange={(e) => setLeaveStatsFilters((f) => ({ ...f, leaveTypeIds: Array.from(e.target.selectedOptions, (opt) => Number(opt.value)) }))} size="sm" className="analytics-multiselect" style={{ minHeight: 76 }}>
                  {leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
                </Form.Select>
                <Form.Text className="text-muted small">{t("app.analytics.ctrlClick")}</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} lg={2} className="analytics-filter-buttons-col">
              <div className="analytics-filter-buttons">
                <Button variant="primary" onClick={applyLeaveStatsFilters} className="analytics-filter-btn" size="sm">{t("app.analytics.apply")}</Button>
                <Button variant="outline-secondary" onClick={clearLeaveStatsFilters} className="analytics-filter-btn" size="sm">{t("app.analytics.clear")}</Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Collapse>
    </Card>
  );

  const renderPieChart = (items, title, getValue, getLabel, noCard = false, cardClassName = "") => {
    if (!items?.length) return null;
    const total = items.reduce((sum, item) => sum + (getValue(item) || 0), 0);
    const slices = items
      .map((item, idx) => ({ label: getLabel(item), value: getValue(item) || 0, idx }))
      .filter((s) => s.value > 0);
    if (slices.length === 0) return null;
    let startAngle = -90;
    const piePaths = slices.map((s) => {
      const angle = total > 0 ? (s.value / total) * 360 : 0;
      const startRad = (Math.PI * startAngle) / 180;
      const endRad = (Math.PI * (startAngle + angle)) / 180;
      const r = 50;
      const x1 = 50 + r * Math.cos(startRad);
      const y1 = 50 + r * Math.sin(startRad);
      const x2 = 50 + r * Math.cos(endRad);
      const y2 = 50 + r * Math.sin(endRad);
      const largeArc = angle > 180 ? 1 : 0;
      const path = `M50,50 L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
      startAngle += angle;
      return { path, color: colors[s.idx % colors.length], label: s.label, value: s.value };
    });
    const content = (
      <div className="analytics-pie-content d-flex flex-column flex-md-row align-items-center align-items-md-start flex-wrap" style={{ gap: 24 }}>
        <div className="d-flex flex-column align-items-center">
          <svg className="analytics-pie-svg" width={220} height={220} viewBox="0 0 100 100" style={{ display: "block", maxWidth: "100%" }}>
            {piePaths.map((s, i) => (
              <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="2" />
            ))}
          </svg>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2" style={{ minWidth: 180 }}>
          {piePaths.map((s, i) => (
            <span key={i} className="d-flex align-items-center gap-2" title={`${s.label}: ${s.value}`}>
              <span style={{ width: 12, height: 12, borderRadius: 2, background: s.color, flexShrink: 0 }} />
              <span className="small">{s.label} ({s.value})</span>
            </span>
          ))}
        </div>
      </div>
    );
    if (noCard) return content;
    const cardClasses = ["shadow-sm", "border-0", "mb-4", "analytics-card", "analytics-pie-chart-card", cardClassName].filter(Boolean).join(" ");
    return (
      <Card className={cardClasses}>
        <Card.Body className="px-2 px-md-3 d-flex flex-column">
          <h5 className="fw-bold mb-3 analytics-section-title">{title}</h5>
          <div className="analytics-pie-chart-body flex-grow-1">{content}</div>
        </Card.Body>
      </Card>
    );
  };

  const getMonthKeyAndLabel = (periodStart) => {
    if (!periodStart || typeof periodStart !== "string") return { key: "_", label: t("app.analytics.other") };
    const parts = periodStart.trim().split(/[-T]/);
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (Number.isNaN(y) || Number.isNaN(m)) return { key: "_", label: t("app.analytics.other") };
    const monthKeys = ["monthJanuary", "monthFebruary", "monthMarch", "monthApril", "monthMay", "monthJune", "monthJuly", "monthAugust", "monthSeptember", "monthOctober", "monthNovember", "monthDecember"];
    const monthLabel = monthKeys[m - 1] ? t(`app.analytics.${monthKeys[m - 1]}`) : t("app.analytics.month");
    const key = `${y}-${String(m).padStart(2, "0")}`;
    const label = `${monthLabel} ${y}`;
    return { key, label };
  };

  const renderWeeklyBarChartByMonth = (items) => {
    if (!items?.length) return null;
    const byMonth = {};
    items.forEach((item) => {
      const { key, label } = getMonthKeyAndLabel(item.periodStart);
      if (!byMonth[key]) byMonth[key] = { monthLabel: label, weeks: [] };
      byMonth[key].weeks.push(item);
    });
    const monthKeys = Object.keys(byMonth).filter((k) => k !== "_").sort();
    if (byMonth._?.weeks?.length) monthKeys.push("_");
    const maxDays = Math.max(...items.map((item) => item.totalLeaveDays || 0), 1);
    const BAR_AREA_HEIGHT = 160;
    let colorIdx = 0;
    return (
      <Row className="mb-4">
        {monthKeys.map((monthKey) => {
          const { monthLabel, weeks } = byMonth[monthKey];
          return (
            <Col key={monthKey} xs={12} sm={6} lg={4} className="mb-4">
              <Card className="shadow-sm border-0 h-100 analytics-card">
                <Card.Body className="px-2 px-md-3">
                  <h6 className="fw-bold mb-3 analytics-weekly-month-title" style={{ fontSize: "0.95rem" }}>{monthLabel}</h6>
                  <div className="analytics-chart-with-axes">
                    <div className="analytics-y-axis" aria-hidden="true">
                      <span className="analytics-y-tick">{maxDays}</span>
                      <span className="analytics-y-tick">0</span>
                    </div>
                    <div className="analytics-chart-inner">
                      <div className="analytics-weekly-month-bars d-flex align-items-end">
                    {weeks.map((item, idx) => {
                      const value = item.totalLeaveDays || 0;
                      const barHeight = maxDays > 0 ? Math.round((value / maxDays) * BAR_AREA_HEIGHT) + 24 : 24;
                      const barColor = colors[colorIdx % colors.length];
                      colorIdx += 1;
                      return (
                        <div key={item.label + idx} className="analytics-weekly-bar-item text-center">
                          <div className="analytics-weekly-bar-column">
                            <div className="analytics-weekly-bar-fill" style={{ height: `${barHeight}px`, background: barColor }} title={`${item.label}: ${item.totalLeaveDays} day(s), ${item.distinctUsersCount} user(s)`} />
                          </div>
                          <div className="analytics-weekly-bar-label">{item.label}</div>
                          <div className="analytics-weekly-bar-meta">{item.totalLeaveDays} day{item.totalLeaveDays !== 1 ? "s" : ""}</div>
                          <div className="analytics-weekly-bar-meta">{item.distinctUsersCount} user{item.distinctUsersCount !== 1 ? "s" : ""}</div>
                        </div>
                      );
                    })}
                      </div>
                      <div className="analytics-x-axis" aria-hidden="true" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderBarChart = (items, title, colorOffset = 0, noCard = false) => {
    if (!items?.length) return null;
    const maxDays = Math.max(...items.map((item) => item.totalLeaveDays || 0), 1);
    const BAR_AREA_HEIGHT = 180;
    const barContent = (
      <div className="analytics-chart-with-axes">
        <div className="analytics-y-axis" aria-hidden="true">
          <span className="analytics-y-tick">{maxDays}</span>
          <span className="analytics-y-tick">0</span>
        </div>
        <div className="analytics-chart-inner">
          <div className="analytics-bar-chart d-flex align-items-end flex-wrap">
            {items.map((item, idx) => {
              const value = item.totalLeaveDays || 0;
              const barHeight = maxDays > 0 ? Math.round((value / maxDays) * BAR_AREA_HEIGHT) + 24 : 24;
              return (
                <div key={item.label + idx} className="text-center analytics-bar-item">
                  <div className="analytics-bar-column">
                    <div className="analytics-bar-fill" style={{ height: `${barHeight}px`, minHeight: 24, background: colors[(idx + colorOffset) % colors.length] }} title={`${item.label}: ${item.totalLeaveDays} day(s), ${item.distinctUsersCount} user(s)`} />
                  </div>
                  <div className="analytics-bar-label">{item.label}</div>
                  <div className="analytics-bar-meta">{item.totalLeaveDays} day{item.totalLeaveDays !== 1 ? "s" : ""}</div>
                  <div className="analytics-bar-meta">{item.distinctUsersCount} user{item.distinctUsersCount !== 1 ? "s" : ""}</div>
                </div>
              );
            })}
          </div>
          <div className="analytics-x-axis" aria-hidden="true" />
        </div>
      </div>
    );
    if (noCard) return barContent;
    return (
      <Card className="shadow-sm border-0 mb-4 analytics-card">
        <Card.Body className="px-2 px-md-3">
          <h5 className="fw-bold mb-3 analytics-section-title">{title}</h5>
          {barContent}
        </Card.Body>
      </Card>
    );
  };

  const renderTab3LeaveStats = () => {
    if (leaveStatsLoading) return <div className="py-5"><FetchingSpinner text={t("app.analytics.loadingStatistics")} /></div>;
    if (leaveStatsError) return <div className="alert alert-danger">{leaveStatsError}</div>;
    if (!leaveStatsData) return <div className="alert alert-info">{t("app.analytics.noDataApplyFilters")}</div>;
    const d = leaveStatsData;
    return (
      <>
        {renderLeaveStatsFilters()}
        {d.leaveStatusOverview?.length > 0 && (
          <Card className="shadow-sm border-0 mb-4 analytics-card">
            <Card.Body className="px-2 px-md-3">
              <h5 className="fw-bold mb-3 analytics-section-title">{t("app.analytics.leaveStatusOverview")}</h5>
              <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                {d.leaveStatusOverview.map((ls, idx) => (
                  <span key={ls.status || idx} className="badge rounded-pill px-3 py-2" style={{ fontSize: "0.9rem", background: statusColors[idx % statusColors.length], color: "#fff" }}>{ls.status || "—"} ({ls.count})</span>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}
        {d.weeklyLeaveStatistics?.length > 0 && (
          <Card className="shadow-sm border-0 mb-4 analytics-card analytics-weekly-group">
            <Card.Header className="bg-light border-0 py-2 px-2 px-md-3">
              <span className="fw-semibold text-secondary small text-uppercase"><i className="bi bi-calendar-week me-2" />{t("app.analytics.weeklyLeaveStatistics")}</span>
            </Card.Header>
            <Card.Body className="pt-2 pb-3 px-2 px-md-3">
              {renderWeeklyBarChartByMonth(d.weeklyLeaveStatistics)}
            </Card.Body>
          </Card>
        )}
        {(d.monthlyLeaveStatistics?.length > 0 || d.leaveTypesOverview?.length > 0) && (
          <>
            <Row className="analytics-pie-cards-row">
              {d.monthlyLeaveStatistics?.length > 0 && (
                <Col xs={12} md={6} className="mb-4 d-flex">
                  {renderPieChart(d.monthlyLeaveStatistics, t("app.analytics.monthlyLeaveStatistics"), (item) => item.totalLeaveDays || 0, (item) => item.label || "", false, "h-100")}
                </Col>
              )}
              {d.leaveTypesOverview?.length > 0 && (
                <Col xs={12} md={6} className="mb-4 d-flex">
                  {renderPieChart(d.leaveTypesOverview, t("app.analytics.leaveTypesOverview"), (item) => item.count || 0, (item) => item.type || item.name || "", false, "h-100")}
                </Col>
              )}
            </Row>
            {d.monthlyLeaveStatistics?.length > 0 && (
              <Row>
                <Col xs={12} className="mb-4">
                  {renderBarChart(d.monthlyLeaveStatistics, t("app.analytics.monthlyLeaveStatistics"), 1)}
                </Col>
              </Row>
            )}
            {d.leaveTypesOverview?.length > 0 && (() => {
              const maxCount = Math.max(...d.leaveTypesOverview.map((lt) => lt.count || 0), 1);
              return (
                <Row>
                  <Col xs={12} className="mb-4">
                    <Card className="shadow-sm border-0 analytics-card">
                      <Card.Body className="px-2 px-md-3">
                        <h5 className="fw-bold mb-3 analytics-section-title">{t("app.analytics.leaveTypesOverview")}</h5>
                        <div className="analytics-chart-with-axes">
                          <div className="analytics-y-axis" aria-hidden="true">
                            <span className="analytics-y-tick">{maxCount}</span>
                            <span className="analytics-y-tick">0</span>
                          </div>
                          <div className="analytics-chart-inner">
                            <div className="analytics-bar-chart analytics-leave-types-bars d-flex align-items-end flex-wrap">
                              {d.leaveTypesOverview.map((lt, idx) => (
                                <div key={lt.type || idx} className="analytics-bar-item analytics-leave-type-item text-center">
                                  <div style={{ height: `${(lt.count || 0) * 10 + 30}px`, background: colors[idx % colors.length], borderRadius: 8, marginBottom: 8 }} />
                                  <div className="fw-semibold" style={{ fontSize: 15 }}>{lt.type}</div>
                                  <div className="text-muted" style={{ fontSize: 13 }}>{lt.count} requests</div>
                                </div>
                              ))}
                            </div>
                            <div className="analytics-x-axis" aria-hidden="true" />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              );
            })()}
          </>
        )}
      </>
    );
  };

  const renderAnalyticsTabs = () => (
    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "dashboard")} className="analytics-tabs mb-3" variant="tabs">
      <Tab eventKey="dashboard" title={<><i className="bi bi-speedometer2 me-1 me-md-2" /><span className="d-none d-sm-inline">{t("app.analytics.dashboard")}</span><span className="d-inline d-sm-none">{t("app.analytics.dashboard")}</span></>}>{renderTab1Dashboard()}</Tab>
      <Tab eventKey="onLeave" title={<><i className="bi bi-calendar-x me-1 me-md-2" /><span className="d-none d-sm-inline">{t("app.analytics.onLeaveToday")}</span><span className="d-inline d-sm-none">{t("app.analytics.onLeaveToday")}</span></>}>{renderTab2OnLeave()}</Tab>
      <Tab eventKey="leaveStats" title={<><i className="bi bi-bar-chart me-1 me-md-2" /><span className="d-none d-sm-inline">{t("app.analytics.leaveStatistics")}</span><span className="d-inline d-sm-none">{t("app.analytics.leaveStatistics")}</span></>}>{renderTab3LeaveStats()}</Tab>
    </Tabs>
  );

  return (
    <div className="analytics-page py-4 px-2 px-sm-3 px-md-4">
      {renderHeader()}
      {renderAnalyticsTabs()}
    </div>
  );
}
